import { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { COMPANY } from "../lib/company";
import type { Partner } from "./partners";

/**
 * The one thing worth interrupting a partner enquiry to say: come through us and
 * the creative work and your plan are quoted as one bundle.
 *
 * It reuses the product's `.modal-backdrop` / `.modal` shell so it is the same
 * object a signed-in user already knows, and it keeps the behaviour contract the
 * shared `ConfirmDialog` established — Escape closes, the backdrop closes — with
 * the focus handling a public, keyboard-reachable dialog also needs:
 *
 *  - focus moves into the dialog on open and is restored to the control that
 *    opened it on close, so a keyboard user is not dumped at the top of the page;
 *  - Tab is trapped, so the dialog cannot be tabbed behind;
 *  - the page behind is locked, so dismissing does not leave you somewhere else.
 *
 * It is not a gate. "Not now" is a real way out, and the enquiry is reachable
 * either way — a modal that can only be escaped by converting is a dark pattern,
 * not a CTA.
 */
export function PartnerBundleDialog({
  partner, onClose,
}: {
  partner: Partner;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const dialogRef = useRef<HTMLDivElement>(null);
  const primaryRef = useRef<HTMLButtonElement>(null);
  /** The control that opened this, so focus can go back where it came from. */
  const openerRef = useRef<Element | null>(null);

  useEffect(() => {
    openerRef.current = document.activeElement;
    primaryRef.current?.focus();
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = overflow;
      (openerRef.current as HTMLElement | null)?.focus?.();
    };
  }, []);

  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Escape") { e.stopPropagation(); onClose(); return; }
    if (e.key !== "Tab") return;
    // Trap: the dialog is the whole tab loop while it is open.
    const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    if (!focusable?.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }, [onClose]);

  return (
    <div className="modal-backdrop" onClick={onClose} onKeyDown={onKeyDown}>
      <div
        ref={dialogRef}
        className={`modal partner-bundle-modal ${partner.plate}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="partner-bundle-title"
        aria-describedby="partner-bundle-body"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="gz-logo-plate">
          <img src={partner.logo} alt={`${partner.name} logo`} width="293" height="50" />
        </p>
        <h2 id="partner-bundle-title">
          Come through {COMPANY.product} and you get a bundle price.
        </h2>
        <div id="partner-bundle-body">
          <p>
            Go to {partner.name} on your own and you pay their standard rate for the
            work, then your publishing separately. Start here instead and we put the
            two together &mdash; {partner.bundle} &mdash; at a partner rate, with one
            conversation and one point of contact.
          </p>
          <ul className="partner-bundle-points">
            <li>
              <span aria-hidden="true">✦</span>
              <span><strong>One quote, not two.</strong> The creative and the plan are priced together.</span>
            </li>
            <li>
              <span aria-hidden="true">◈</span>
              <span><strong>A studio that knows your channels.</strong> They already make for the formats you publish to.</span>
            </li>
            <li>
              <span aria-hidden="true">◷</span>
              <span><strong>We make the introduction.</strong> You brief once; we pass it on the same day.</span>
            </li>
          </ul>
          <p className="partner-bundle-small">
            Nothing is committed here. We will put the exact figure in writing before
            you decide, and you are free to approach {partner.name} directly instead.
          </p>
        </div>
        <div className="row modal-actions partner-bundle-actions">
          <button
            ref={primaryRef}
            className="primary"
            onClick={() => { onClose(); navigate(`/contact?partner=${partner.key}`); }}
          >
            Get the bundle price <span aria-hidden="true">&rarr;</span>
          </button>
          <button onClick={onClose}>Not now</button>
        </div>
      </div>
    </div>
  );
}
